import { useEffect, useMemo, useState } from "react";
import api, { apiErr, API_BASE } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Button } from "@/components/ui/button";
import { BookOpen, FileDown, Trash2, Clock, FileText } from "lucide-react";
import { toast } from "sonner";

const CLASSES = ["Play Group", "LKG", "UKG", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const SECTIONS = ["A", "B"];

export default function Homework() {
  const { user } = useAuth();
  const isAdminLike = user?.role === "admin" || user?.role === "super_admin";
  const assignedPairs = useMemo(() => {
    if (isAdminLike) return CLASSES.flatMap((c) => SECTIONS.map((s) => [c, s]));
    return (user?.assignments || []).map((a) => [a.class_name, a.section]);
  }, [user, isAdminLike]);

  const initialClass = assignedPairs[0]?.[0] || "5";
  const initialSection = assignedPairs[0]?.[1] || "A";
  const [classFilter, setClassFilter] = useState(initialClass);
  const [sectionFilter, setSectionFilter] = useState(initialSection);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const validSections = useMemo(() => {
    const set = new Set(assignedPairs.filter((p) => p[0] === classFilter).map((p) => p[1]));
    return Array.from(set);
  }, [assignedPairs, classFilter]);

  useEffect(() => {
    if (validSections.length && !validSections.includes(sectionFilter)) {
      setSectionFilter(validSections[0]);
    }
  }, [validSections, sectionFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/homework", {
        params: { class_name: classFilter, section: sectionFilter, date },
      });
      setItems(data);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load homework");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [classFilter, sectionFilter, date]);

  const remove = async (id) => {
    if (!window.confirm("Delete this homework? This cannot be undone.")) return;
    try {
      await api.delete(`/homework/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not delete");
    }
  };

  const grouped = useMemo(() => {
    const byPeriod = {};
    for (const it of items) {
      (byPeriod[it.period] = byPeriod[it.period] || []).push(it);
    }
    return Object.entries(byPeriod).sort((a, b) => Number(a[0]) - Number(b[0]));
  }, [items]);

  const token = (typeof window !== "undefined" && localStorage.getItem("sa_token")) || "";

  // The user's access_token cookie carries auth on the GET request the browser
  // makes when clicking the link, so we don't need to add anything else.
  void token;

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Homework</div>
          <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>Homework given</h1>
          <p className="text-slate-500 mt-1">Browse what was assigned in each period for any class · section · date.</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Class</label>
            <Select value={classFilter} onValueChange={setClassFilter}>
              <SelectTrigger className="h-10 rounded-xl w-28" data-testid="hw-class-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {(isAdminLike ? CLASSES : Array.from(new Set(assignedPairs.map((p) => p[0])))).map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Section</label>
            <Select value={sectionFilter} onValueChange={setSectionFilter}>
              <SelectTrigger className="h-10 rounded-xl w-24" data-testid="hw-section-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {(isAdminLike ? SECTIONS : validSections).map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Date</label>
            <DatePicker value={date} onChange={setDate} className="h-10 rounded-xl w-44" data-testid="hw-date-input"/>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500" data-testid="hw-empty">
          No homework recorded for class {classFilter}-{sectionFilter} on {date}.
        </div>
      ) : (
        <div className="space-y-4" data-testid="hw-list">
          {grouped.map(([period, rows]) => (
            <div key={period} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`hw-period-${period}`}>
              <div className="px-5 py-3 border-b border-slate-100 bg-sky-50/40 flex items-center gap-2">
                <Clock className="h-4 w-4 text-sky-500" />
                <div className="text-sm font-semibold text-slate-900" style={{fontFamily:"Outfit"}}>Period {period}</div>
                <span className="text-xs text-slate-500">· {rows[0].subject}</span>
                <span className="ml-auto text-xs text-slate-400">{rows.length} item{rows.length === 1 ? "" : "s"}</span>
              </div>
              <ul className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const canDelete = isAdminLike || r.created_by_id === user?.id;
                  const downloadUrl = `${API_BASE}/homework/${r.id}/attachment`;
                  return (
                    <li key={r.id} className="px-5 py-3 flex items-start gap-3" data-testid={`hw-item-${r.id}`}>
                      <div className="h-9 w-9 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center shrink-0">
                        <BookOpen className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900">{r.title}</span>
                          <span className="text-xs text-slate-500">by {r.created_by_name}</span>
                          <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleString()}</span>
                        </div>
                        {r.description && (
                          <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{r.description}</p>
                        )}
                        {r.has_attachment && (
                          <a
                            href={downloadUrl}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`hw-download-${r.id}`}
                            className="inline-flex items-center gap-1.5 mt-2 text-sm text-sky-600 hover:text-sky-700 hover:underline"
                          >
                            <FileText className="h-3.5 w-3.5" />
                            {r.attachment_name}
                            <span className="text-xs text-slate-400">· {(r.attachment_size/1024).toFixed(1)} KB</span>
                            <FileDown className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                      {canDelete && (
                        <Button onClick={() => remove(r.id)} variant="ghost" size="sm" className="text-rose-500 hover:bg-rose-50" data-testid={`hw-delete-${r.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
